import { createPortal } from "react-dom";
import { useNavigate } from "react-router-dom";

/** 四步進度（等待安排瓦斯行的旅程；前兩步已完成）。 */
const STEPS: Array<{ title: string; desc?: string; state: "done" | "now" | "todo" }> = [
  { title: "完成註冊", state: "done" },
  { title: "完成加入設定", desc: "鋼瓶、配件與合約資料已備妥", state: "done" },
  { title: "對接瓦斯行", desc: "平台正在為您的地區安排合作瓦斯行", state: "now" },
  { title: "開始訂購瓦斯", desc: "對接完成即開放，資料免重填", state: "todo" },
];

/**
 * 「等待安排瓦斯行」客戶完成加入設定後的收尾頁（waiting-arrangement mockup B）：
 * 綠勾（你的部分完成）＋角落琥珀小時鐘（平台對接進行中）。全屏 portal，蓋在 FirstJoinSetup 上。
 * 按鈕自己導回 /home（onGoHome 只負責關掉設定流程）——上層的 onDone 可能導向
 * 訂購頁（/gasShop/products），等待客戶會立刻撞上訂購鎖定遮罩，看起來像壞掉。
 */
const WaitingSetupDone = ({ onGoHome }: { onGoHome: () => void }) => {
  const navigate = useNavigate();
  const goHome = () => {
    onGoHome();
    navigate("/home", { replace: true });
  };
  return createPortal(
    <div className="fixed inset-0 z-[1000006] flex flex-col overflow-hidden bg-[#2196f3]">
      <div className="shrink-0 text-center text-white pt-[56px] pb-[100px]">
        {/* 品牌小標移除。保留同樣式的空行（&nbsp;）而非寫死高度：行高完全等同原本那行，
            藍色頂區高度與跨界徽章位置不會位移。 */}
        <div className="text-[11px] font-extrabold tracking-[5px] opacity-80" aria-hidden>
          &nbsp;
        </div>
      </div>
      <div className="flex-1 min-h-0 bg-white rounded-t-[32px] flex flex-col px-6 pb-7 overflow-y-auto">
        <div className="flex justify-center -mt-[50px] mb-3">
          <div className="w-[94px] h-[94px] rounded-full bg-white flex items-center justify-center" style={{ boxShadow: "0 14px 34px -10px rgba(33,150,243,.55)" }}>
            <div className="w-[68px] h-[68px] rounded-full flex items-center justify-center relative" style={{ background: "linear-gradient(145deg,#34D399,#10B981)" }}>
              <svg width="32" height="32" viewBox="0 0 24 24" fill="none">
                <path d="M5 12l5 5L20 7" stroke="#fff" strokeWidth="3" strokeLinecap="round" strokeLinejoin="round" />
              </svg>
              <div className="absolute -right-1 -bottom-1 w-[28px] h-[28px] rounded-full bg-white flex items-center justify-center" style={{ boxShadow: "0 2px 7px rgba(0,0,0,.16)" }}>
                <svg width="16" height="16" viewBox="0 0 24 24" fill="none">
                  <circle cx="12" cy="12" r="8.5" stroke="#F59E0B" strokeWidth="2" />
                  <path d="M12 7.5V12l3 2" stroke="#F59E0B" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
                </svg>
              </div>
            </div>
          </div>
        </div>

        <div className="text-[22px] font-black text-[#0f1a35] text-center">資料已備妥！</div>
        <div className="text-[13.5px] text-[#64748B] leading-[1.7] text-center mt-2 px-1">
          我們會<span className="font-bold text-[#0f1a35]">盡快為您對接附近的瓦斯行</span>，完成後透過 LINE 通知您，屆時即可直接開始訂購。
        </div>

        <div className="bg-[#F6F9FE] border border-[#EAF1FB] rounded-[16px] px-4 pt-4 pb-3 mt-5">
          {STEPS.map((s, i) => (
            <div key={s.title} className="flex gap-3">
              <div className="flex flex-col items-center w-[22px] shrink-0">
                {s.state === "done" ? (
                  <div className="w-[22px] h-[22px] rounded-full bg-[#22C55E] flex items-center justify-center shrink-0">
                    <svg width="12" height="12" viewBox="0 0 24 24" fill="none">
                      <path d="M5 12l5 5L20 7" stroke="#fff" strokeWidth="3.4" strokeLinecap="round" strokeLinejoin="round" />
                    </svg>
                  </div>
                ) : (
                  <div
                    className={`w-[22px] h-[22px] rounded-full flex items-center justify-center text-[12px] font-black shrink-0 ${
                      s.state === "now" ? "bg-[#FEF3C7] border-2 border-[#F59E0B] text-[#B45309]" : "bg-[#EDF2F9] text-[#94A3B8]"
                    }`}
                  >
                    {i + 1}
                  </div>
                )}
                {i < STEPS.length - 1 && (
                  <div className={`w-[2px] flex-1 min-h-[12px] my-[3px] ${s.state === "done" ? "bg-[#22C55E]" : "bg-[#E3ECF7]"}`} />
                )}
              </div>
              <div className={i < STEPS.length - 1 ? "pb-3.5 pt-px" : "pb-0.5 pt-px"}>
                <div className={`text-[13.5px] font-extrabold ${s.state === "todo" ? "text-[#94A3B8]" : "text-[#0f1a35]"}`}>
                  {s.title}
                  {s.state === "now" && (
                    <span className="inline-block ml-1.5 text-[10.5px] font-extrabold text-[#B45309] bg-[#FEF3C7] rounded-full px-2 py-0.5 align-[1px]">進行中</span>
                  )}
                </div>
                {s.desc && <div className="text-[11.5px] text-[#94A3B8] mt-0.5 leading-snug">{s.desc}</div>}
              </div>
            </div>
          ))}
        </div>

        <div className="flex items-center gap-2.5 bg-[#F0FAF4] border border-[#D7F0E1] rounded-[14px] px-3.5 py-3 mt-3">
          <div className="w-[30px] h-[30px] rounded-[9px] bg-[#22C55E] flex items-center justify-center shrink-0">
            <svg width="16" height="16" viewBox="0 0 24 24" fill="none">
              <path d="M12 4c-4.4 0-8 2.9-8 6.5 0 2.1 1.2 3.9 3.1 5.1-.1.5-.5 1.7-.6 2 0 0-.1.3.1.4.2.1.4 0 .4 0 .5-.1 2.2-1 3.1-1.5.6.1 1.2.2 1.9.2 4.4 0 8-2.9 8-6.5S16.4 4 12 4z" fill="#fff" />
            </svg>
          </div>
          <div className="text-[12px] text-[#1B7A46] leading-snug font-semibold">對接完成會透過 LINE 訊息通知您，不需要重新填寫資料。</div>
        </div>

        <div className="flex-1" />
        <button onClick={goHome} className="w-full py-4 rounded-[16px] border-none text-white text-base font-bold cursor-pointer mt-5 bg-[#2196f3]">
          回首頁
        </button>
      </div>
    </div>,
    document.body,
  );
};

export default WaitingSetupDone;
